import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { fmtEur } from "../../lib/format";
import { PiggyBank, AlertTriangle, Loader2, FileText } from "lucide-react";

// Saldo TFR del dipendente mostrato all'ufficio quando registra/approva un anticipo TFR (mai al dipendente).
export const TfrBalanceBox = ({ employeeId, year, amount }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!employeeId) { setData(null); return; }
        let cancelled = false;
        setLoading(true);
        api.get(`/tfr/balance/${employeeId}`, { params: year ? { year } : {} })
            .then(({ data: d }) => { if (!cancelled) setData(d); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [employeeId, year]);

    if (!employeeId) return null;
    if (loading && !data) return <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="tfr-balance-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saldo TFR…</div>;
    if (!data) return null;

    if (!data.has_data) {
        return (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="tfr-balance-missing">
                <b>Saldo TFR non caricato</b> per il {data.year}: inserisci il fondo al {data.prev_year_end} nella pagina{" "}
                <Link to="/acconti/tfr" className="font-semibold underline">Fondo TFR</Link>.
                {data.anticipi_count > 0 && <> Anticipi TFR già registrati nel {data.year}: <b>{fmtEur(data.anticipi)}</b> ({data.anticipi_count}).</>}
            </div>
        );
    }

    const amt = parseFloat(amount) || 0;
    const available = data.portal_amounts_net ? data.netto : data.residuo;
    const over = amt > 0 && amt > available + 0.005;
    const nItems = data.anticipi_count + (data.n_cedolini_senza_ricevuta || 0);
    const precision = nItems === 0 ? null : data.precision === "preciso"
        ? { cls: "bg-emerald-100 text-emerald-800", label: `lordo esatto da ${data.n_cedolini} cedolin${data.n_cedolini === 1 ? "o" : "i"}` }
        : data.precision === "stimato"
            ? { cls: "bg-amber-100 text-amber-900", label: `lordo stimato al ${data.aliquota}% (cedolini non ancora in archivio)` }
            : { cls: "bg-amber-100 text-amber-900", label: `${data.n_cedolini} da cedolino · ${data.n_stimati} stimat${data.n_stimati === 1 ? "o" : "i"} al ${data.aliquota}%` };
    return (
        <div className={`rounded-md border p-3 text-xs ${over ? "border-red-300 bg-red-50" : "border-sky-200 bg-sky-50/60"}`} data-testid="tfr-balance-box">
            <div className="mb-2 flex items-center gap-2 font-bold uppercase tracking-wide text-[#15498F]">
                <PiggyBank className="h-4 w-4" /> Saldo TFR {data.year}
                {precision && <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal ${precision.cls}`} data-testid="tfr-balance-precision">{precision.label}</span>}
                <span className="ml-auto font-normal normal-case text-slate-500">aliquota {data.aliquota}%</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
                <Kv label={`Fondo al ${data.prev_year_end}`} value={fmtEur(data.fondo)} testid="tfr-balance-fondo" />
                <Kv label={`Anticipi ${data.year} (${data.anticipi_count})`} value={fmtEur(data.anticipi)} sub={`lordo scalato ${fmtEur(data.anticipi_lordo)}`} testid="tfr-balance-anticipi" />
                <Kv label="Residuo lordo" value={fmtEur(data.residuo)} testid="tfr-balance-residuo" strong />
                <Kv label="Netto disponibile" value={fmtEur(data.netto)} testid="tfr-balance-netto" strong />
            </div>
            {data.orphan_payslips?.length > 0 && (
                <div className="mt-2 flex items-start gap-1.5 text-amber-900" data-testid="tfr-balance-orphans">
                    <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{data.orphan_payslips.length} cedolin{data.orphan_payslips.length === 1 ? "o" : "i"} TFR in archivio senza ricevuta nel portale ({data.orphan_payslips.map((s) => `${String(s.month).padStart(2, "0")}/${data.year}: ${fmtEur(s.lordo)} lordo`).join(", ")}): già scalat{data.orphan_payslips.length === 1 ? "o" : "i"} dal fondo.</span>
                </div>
            )}
            {data.disponibile_delta !== null && data.disponibile_delta !== undefined && (
                <div className="mt-2 flex items-start gap-1.5 text-amber-900" data-testid="tfr-balance-disp-mismatch">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>Il cedolino riporta {fmtEur(data.disponibile_cedolino)} disponibili al {data.disponibile_at} ({data.disponibile_delta > 0 ? "+" : ""}{fmtEur(data.disponibile_delta)} rispetto al saldo caricato): fa fede il saldo del portale.</span>
                </div>
            )}
            {over && (
                <div className="mt-2 flex items-start gap-2 font-semibold text-red-700" data-testid="tfr-balance-warning">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>L'importo di {fmtEur(amt)} supera il TFR {data.portal_amounts_net ? "netto" : "lordo"} disponibile ({fmtEur(available)}). Puoi comunque procedere.</span>
                </div>
            )}
        </div>
    );
};

const Kv = ({ label, value, sub, testid, strong }) => (
    <div data-testid={testid}>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`font-mono-numbers ${strong ? "text-sm font-bold text-slate-800" : "text-slate-700"}`}>{value}</div>
        {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
);
